import { useState, useEffect, useCallback } from "react";
import { Loader2, FileText, Upload, Download, Eye, ClipboardList } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { authFetch } from "@/lib/api";
import { toast } from "sonner";
import { API_BASE } from "./constants";

export default function DocumentsTab({ parcelId, parcelName }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const navigate = useNavigate();

  const fetchDocs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API_BASE}/phyto/parcel/${parcelId}/documents`);
      if (res.ok) setDocuments(await res.json());
    } catch (err) { console.error('[DocumentsTab] fetch failed:', err); }
    setLoading(false);
  }, [parcelId]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const downloadPdf = async (url, filename) => {
    setDownloading(true);
    try {
      const res = await authFetch(url);
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const u = window.URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = u; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(u);
      toast.success("PDF telecharge");
    } catch { toast.error("Erreur de telechargement"); }
    setDownloading(false);
  };

  const handleUpload = () => {
    const input = document.createElement("input");
    input.type = "file"; input.accept = "image/*,.pdf,.doc,.docx";
    input.onchange = async (e) => {
      const file = e.target.files[0]; if (!file) return;
      if (file.size > 100 * 1024 * 1024) { toast.error("Fichier trop volumineux (max 100 Mo)"); return; }
      setUploading(true);
      try {
        const fd = new FormData(); fd.append("file", file); fd.append("parcel_id", parcelId); fd.append("doc_type", "dossier_parcelle");
        const res = await authFetch(`${API_BASE}/phyto/documents/upload`, { method: "POST", body: fd });
        if (!res.ok) { const err = await res.json(); throw new Error(err.detail); }
        toast.success("Document ajoute"); fetchDocs();
      } catch (e) { toast.error(e.message || "Erreur d'upload"); }
      setUploading(false);
    };
    input.click();
  };

  const handleViewDoc = (docId) => {
    const token = localStorage.getItem("session_token");
    window.open(`${API_BASE}/phyto/documents/${docId}/download?token=${token}`, "_blank");
  };

  return (
    <div className="flex flex-col h-full">
      <div className="p-3 border-b border-black/5 space-y-2">
        <div className="flex gap-2">
          <Button size="sm" className="flex-1 h-9 text-[11px] font-semibold rounded-lg text-white gap-1.5" style={{ background: "#2D6A4F" }}
            onClick={() => navigate(`/parcelles/${encodeURIComponent(parcelId)}/fiche`)} data-testid="ouvrir-fiche-parcelle-btn">
            <ClipboardList className="w-3.5 h-3.5" /> Fiche parcelle
          </Button>
          <Button size="sm" variant="outline" className="flex-1 h-9 text-[11px] font-semibold rounded-lg gap-1.5 border-black/10"
            onClick={() => downloadPdf(`${API_BASE}/phyto/parcel/${parcelId}/pdf`, `dossier_phyto_${parcelId}.pdf`)} disabled={downloading} data-testid="export-phyto-pdf-btn">
            {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} Dossier phyto PDF
          </Button>
        </div>
        <Button size="sm" variant="outline" className="w-full h-9 text-[11px] font-semibold rounded-lg gap-1.5 border-black/10"
          onClick={handleUpload} disabled={uploading} data-testid="upload-doc-btn">
          {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Ajouter un document (max 100 Mo)
        </Button>
      </div>
      <ScrollArea className="flex-1">
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin" style={{ color: "#2D6A4F" }} /></div>
        ) : documents.length === 0 ? (
          <div className="text-center py-16 px-4">
            <FileText className="w-8 h-8 mx-auto mb-3 opacity-20" />
            <p className="text-xs" style={{ color: "#7A7A7A" }}>Aucun document uploade</p>
          </div>
        ) : (
          <div className="p-3 space-y-2">
            <p className="text-[10px] uppercase tracking-wider font-medium" style={{ color: "#4A4A4A" }}>{documents.length} document{documents.length > 1 ? 's' : ''}</p>
            {documents.map(doc => (
              <div key={doc.document_id} className="flex items-center justify-between p-3 rounded-xl border border-black/5" data-testid={`doc-${doc.document_id}`}>
                <div className="flex-1 min-w-0 mr-3">
                  <p className="text-xs font-medium truncate" style={{ color: "#1A1A1A" }}>{doc.original_filename}</p>
                  <p className="text-[10px]" style={{ color: "#7A7A7A" }}>{(doc.size / 1024).toFixed(0)} Ko — {new Date(doc.created_at).toLocaleDateString("fr-FR")}</p>
                </div>
                <Button size="sm" variant="outline" className="h-7 text-[10px] gap-1 rounded-lg shrink-0"
                  onClick={() => handleViewDoc(doc.document_id)} data-testid={`view-doc-${doc.document_id}`}>
                  <Eye className="w-3 h-3" /> Voir
                </Button>
              </div>
            ))}
          </div>
        )}
      </ScrollArea>
    </div>
  );
}
